// @flow

import * as React from "react";
import axios from "axios";
import {toast} from "react-toastify";
import {CRISP_WAIT_MS, REVIEW_CLOSE_DELAY_MS, REVIEW_STAR_COUNT, REVIEW_STAR_GOLD, REVIEW_STAR_EMPTY, LOW_RATING_REPLY,
        LOW_RATING_REPLY_FALLBACK_MS, APP_STORE_REVIEW_URL} from "../Constants";

// The review is done when this Crisp session carries the visitor data daf = "true" (set from Crisp once they've
// reviewed us). Crisp loads asynchronously and window.$crisp is a plain array until then, so every access is guarded.
export function crispSaysReviewed(): boolean {
    try {
        const crisp = window.$crisp;
        if (!crisp || typeof crisp.get !== "function") {
            return false;
        }
        const value = crisp.get("session:data", "daf");
        return value === true || String(value).toLowerCase() === "true";
    } catch (ex) {
        return false;
    }
}

// Whether Crisp has loaded and has its session (and with it the session data daf is read from). Until then
// window.$crisp is the plain array index.html created, so the answer is "not yet".
export function crispSessionLoaded(): boolean {
    try {
        const crisp = window.$crisp;
        return !!(crisp && typeof crisp.get === "function" && crisp.get("session:identifier"));
    } catch (ex) {
        return false;
    }
}

// Clicking "Amazing!" (setup guide) or 5 stars (rating modal) opens the App Store review page and marks the review as
// done for the rest of this browser session, per company: a person in two companies still gets asked in the other.
// sessionStorage can be unavailable (private mode, blocked storage), so every access is guarded.
export function reviewClickedKey(realmId: ?string): string {
    return "scan2pay.reviewClicked." + (realmId || "none");
}

export function reviewClickedThisSession(realmId: ?string): boolean {
    return readSessionFlag(reviewClickedKey(realmId));
}

export function markReviewClickedThisSession(realmId: ?string) {
    writeSessionFlag(reviewClickedKey(realmId));
}

// Any star (1-5) answers the rating modal for the rest of this browser session, so it doesn't come back on the next
// dashboard visit or QR invoice a minute later. Closing it with the X doesn't count: it shows again next time.
function ratedKey(realmId: ?string): string {
    return "scan2pay.reviewPromptRated." + (realmId || "none");
}

function readSessionFlag(key: string): boolean {
    try {
        return window.sessionStorage.getItem(key) === "true";
    } catch (ex) {
        return false;
    }
}

function writeSessionFlag(key: string) {
    try {
        window.sessionStorage.setItem(key, "true");
    } catch (ex) {
        // storage blocked: the modal is still gone for as long as this page is open
    }
}

// Only on production (review_prompt_enabled: ENVIRONMENT is "Production"), and only companies in good standing are asked
// to rate us: on the Free plan or one of the plans for sale (plan_is_offered; legacy plans get the "QRify is now Scan2Pay"
// modal instead), and not frozen by a failed payment. A company that has already reviewed us (has_left_review, set by us
// in the database) is never asked again.
export function isEligibleForReviewPrompt(settings: ?{+review_prompt_enabled?: boolean, +plan_is_offered?: boolean, +is_active?: boolean,
                                                      +has_left_review?: boolean}): boolean {
    return !!settings && settings.review_prompt_enabled === true && settings.plan_is_offered === true && settings.is_active !== false
        && settings.has_left_review !== true;
}

// Sent to our Crisp inbox as the visitor on a 1-4 star rating, so the conversation starts on our side with the rating in it.
function lowRatingVisitorMessage(stars: number): string {
    return "Hi there, I've just rated Scan2Pay with " + stars + (stars === 1 ? " star" : " stars") + " :)";
}

type Props = {|
  +realmId: ?string,
  // Where it was shown, for the #apps-reviews Slack message (REVIEW_RATING_SOURCES in controllers.py)
  +source: "dashboard" | "qr_ready",
  // Called after a 5-star click (the App Store review page has been opened)
  +onFiveStars?: () => void,
|};

type State = {|
  visible: boolean,
  canClose: boolean,
  hoveredStars: number,
|};

function Star({filled}: {|+filled: boolean|}): React.Node {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 2.5l2.94 5.96 6.58.96-4.76 4.64 1.12 6.55L12 17.52l-5.88 3.09 1.12-6.55L2.48 9.42l6.58-.96L12 2.5z"
                  fill={filled ? REVIEW_STAR_GOLD : "none"} stroke={filled ? REVIEW_STAR_GOLD : REVIEW_STAR_EMPTY} strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
    );
}

// "How are we doing?" with 5 stars. The caller renders it only for an eligible company (isEligibleForReviewPrompt) and
// remounts it for every new occasion (each dashboard visit, each generated QR invoice). It waits for Crisp's session
// first, so a visitor whose Crisp data says they've already reviewed us (daf) never sees it flash up and disappear.
// 5 stars opens the App Store review page; 1-4 stars opens the Crisp chat (with a message saying how they rated us) so we
// hear what went wrong instead.
class ReviewPromptModal extends React.Component<Props, State> {
    crispPoll: IntervalID;
    crispTimeout: TimeoutID;
    closeTimer: TimeoutID;
    settled: boolean = false;

    constructor(props: Props) {
        super(props);
        this.state = {visible: false, canClose: false, hoveredStars: 0};
        (this: any).rate = this.rate.bind(this);
        (this: any).close = this.close.bind(this);
    }

    componentDidMount() {
        if (readSessionFlag(ratedKey(this.props.realmId)) || reviewClickedThisSession(this.props.realmId)) {
            return;
        }
        if (crispSessionLoaded()) {
            this.crispSessionSettled();
            return;
        }
        // Polling only (no $crisp "on session:loaded" handler): the dashboard's setup guide already listens for that
        // event, and a second handler for the same event could replace it.
        this.crispPoll = setInterval(() => {
            if (crispSessionLoaded()) {
                this.crispSessionSettled();
            }
        }, 250);
        this.crispTimeout = setTimeout(() => this.crispSessionSettled(), CRISP_WAIT_MS);
    }

    componentWillUnmount() {
        clearInterval(this.crispPoll);
        clearTimeout(this.crispTimeout);
        clearTimeout(this.closeTimer);
    }

    crispSessionSettled() {
        if (this.settled) {
            return;
        }
        this.settled = true;
        clearInterval(this.crispPoll);
        clearTimeout(this.crispTimeout);
        if (crispSaysReviewed()) {
            return;
        }
        this.setState({visible: true});
        this.closeTimer = setTimeout(() => this.setState({canClose: true}), REVIEW_CLOSE_DELAY_MS);
    }

    rate(stars: number) {
        writeSessionFlag(ratedKey(this.props.realmId));
        // Every star goes to #apps-reviews. Fire and forget: the rating must work even if this fails, and the person
        // has nothing to retry, so a failure is not shown to them.
        axios.post(window.SERVER_URL + '/review_prompt_rating', {stars: stars, source: this.props.source},
                   {headers: {"X-Requested-With": "XMLHttpRequest"}}).catch(() => {});
        try {
            window.$crisp.push(["set", "session:event", [[["qrify:rated", {stars: stars}, stars === REVIEW_STAR_COUNT ? "green" : "orange"]]]]);
        } catch (ex) {
            // Crisp not loaded: the rating itself still works
        }

        if (stars === REVIEW_STAR_COUNT) {
            window.open(APP_STORE_REVIEW_URL, "_blank", "noopener,noreferrer");
            markReviewClickedThisSession(this.props.realmId);
            if (this.props.onFiveStars) {
                this.props.onFiveStars();
            }
        } else {
            try {
                if (!window.$crisp || typeof window.$crisp.get !== "function") {
                    throw new Error("Crisp is not loaded");
                }
                // The visitor's "I've just rated Scan2Pay with N stars" is sent to our inbox (message:send); our reply
                // asking what to improve is only displayed in their chat window (message:show), not sent. message:show
                // appears instantly while the sent message only appears once Crisp's servers have it, so showing both
                // at once put our reply above their message. The reply waits for Crisp's message:sent event instead
                // (with a fallback in case it never fires), and the handler is removed once it has run.
                const crisp = window.$crisp;
                let replyShown = false;
                const showReply = () => {
                    if (replyShown) {
                        return;
                    }
                    replyShown = true;
                    try {
                        crisp.push(["off", "message:sent"]);
                        crisp.push(["do", "message:show", ["text", LOW_RATING_REPLY]]);
                    } catch (ex) {
                        // the visitor's message already went out; the reply is a nicety
                    }
                };
                crisp.push(["on", "message:sent", showReply]);
                crisp.push(["do", "chat:open"]);
                crisp.push(["do", "message:send", ["text", lowRatingVisitorMessage(stars)]]);
                setTimeout(showReply, LOW_RATING_REPLY_FALLBACK_MS);
            } catch (ex) {
                toast.success("Thank you for your feedback!");
            }
        }
        this.close();
    }

    close() {
        clearTimeout(this.closeTimer);
        this.setState({visible: false});
    }

    render(): React.Node {
        if (!this.state.visible) {
            return null;
        }
        const stars = [];
        for (let n = 1; n <= REVIEW_STAR_COUNT; n++) {
            stars.push(
                <button key={n} type="button" className="review-prompt-star" aria-label={n === 1 ? "1 star" : n + " stars"}
                        onMouseEnter={() => this.setState({hoveredStars: n})} onFocus={() => this.setState({hoveredStars: n})}
                        onClick={() => this.rate(n)}>
                    <Star filled={n <= this.state.hoveredStars} />
                </button>
            );
        }
        // No close on an overlay click: until the X appears, a star is the only way out.
        return (
            <div className="app-modal-overlay">
                <div className="app-modal-dialog review-prompt-dialog" role="dialog" aria-modal="true" aria-labelledby="review-prompt-title">
                    {this.state.canClose ? (
                        <button type="button" className="app-modal-close review-prompt-close" aria-label="Close" onClick={this.close}>&times;</button>
                    ) : null}
                    <h2 id="review-prompt-title" className="review-prompt-title">How are we doing?</h2>
                    <p className="review-prompt-text">Tap a star to rate your experience with Scan2Pay.</p>
                    <div className="review-prompt-stars" onMouseLeave={() => this.setState({hoveredStars: 0})}>
                        {stars}
                    </div>
                </div>
            </div>
        );
    }
}

export default ReviewPromptModal;
